import type { Server } from 'restaurantAdmin/reservations/service/apiHelpers';
import type { GuestTag } from 'restaurantAdmin/restaurants/apiHelpers';
import type {
  LightspeedOrder,
  ReservationStatus,
  TransactionDetails,
} from '@shared/types/reservations';
import { api } from '../api';

export interface CreatedGuestNote {
  id: string;
}

export interface GuestNoteResponse {
  data: GuestNote | null;
}

export interface GuestNote {
  id: string;
  note: string;
  updatedBy: string | null;
}

export interface CancellationFeeAttributes {
  price?: number;
  receiptNumber?: string;
  processedAt?: string;
}

export interface GuestReservation {
  cancellationFeeTransaction: CancellationFeeAttributes | null;
  staffNote: string | null;
  date: string;
  guestCount: number;
  id: string;
  lightspeedOrder?: LightspeedOrder | null;
  publicName: string;
  seatedTableName: string | null;
  server: Server | null;
  status: ReservationStatus;
  time: string;
  transactionDetails: TransactionDetails;
}

export interface GuestsApiGuest {
  email?: string;
  firstName: string;
  hasVipTier: boolean;
  id: string;
  isVip: boolean;
  lastName: string;
  phone?: string;
}

export interface GuestExportRow {
  email: string | null;
  firstName: string;
  firstReservation: string | null;
  guestCountForLastReservation: number | null;
  guestNotes: string | null;
  guestSpend: number;
  guestTags: string[];
  lastName: string;
  lastReservation: string | null;
  mostRecentTableSelection: string | null;
  numberOfTotalVisits: number;
  phone: string | null;
  reservationNotes: string | null;
  restaurantName: string;
  restaurantNotes: string | null;
}

export const createGuestNotes = async ({
  restaurantId,
  guestId,
  note,
}: {
  restaurantId: string;
  guestId: string;
  note: string;
}): Promise<CreatedGuestNote> =>
  api.post(`/restaurants/${restaurantId}/guests/${guestId}/guest-notes`, {
    note,
  });

const ALLOWED_GUEST_FILTER_PARAMS = [
  'startDate',
  'endDate',
  'tag',
  'vipTierId',
  'marketingOptedInOnly',
];

const buildGuestFilterRequestParams = ({
  filters,
  params = new URLSearchParams(),
  search,
}: {
  filters: URLSearchParams;
  params?: URLSearchParams;
  search: string;
}): URLSearchParams => {
  const apiRequestParams = params;

  filters.forEach((value, key) => {
    if (ALLOWED_GUEST_FILTER_PARAMS.includes(key)) {
      // Only `append` will preserve multiple values for the same key (e.g. tag)
      apiRequestParams.append(key, value);
    }
  });

  if (search.trim() !== '') {
    apiRequestParams.append('search', search.trim());
  }

  return apiRequestParams;
};

export const getGuests = async ({
  restaurantId,
  limit,
  offset,
  search,
  filters,
}: {
  restaurantId: string;
  limit: number;
  offset: number;
  search: string;
  filters: URLSearchParams;
}): Promise<GuestsApiGuest[]> => {
  const apiRequestParams = buildGuestFilterRequestParams({
    filters,
    params: new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    }),
    search,
  });

  return api.get(`/restaurants/${restaurantId}/guests?${apiRequestParams}`);
};

export const getGuestExportRows = async ({
  restaurantId,
  search,
  filters,
}: {
  restaurantId: string;
  search: string;
  filters: URLSearchParams;
}): Promise<GuestExportRow[]> => {
  const apiRequestParams = buildGuestFilterRequestParams({
    filters,
    search,
  });

  return api.get(
    `/restaurants/${restaurantId}/guests/export?${apiRequestParams}`,
  );
};

export const getGuest = (
  restaurantId: string,
  guestId: string,
): Promise<GuestsApiGuest> =>
  api.get(`/restaurants/${restaurantId}/guests/${guestId}`);

export const getGuestNotes = async (
  restaurantId: string,
  guestId: string,
  signal?: AbortSignal,
): Promise<GuestNoteResponse> =>
  api.get(`/restaurants/${restaurantId}/guests/${guestId}/guest-notes`, {
    signal,
  });

export const getGuestTagsForGuest = (
  restaurantId: string,
  guestId: string,
): Promise<GuestTag[]> =>
  api.get(`/restaurants/${restaurantId}/guests/${guestId}/tags`);

export const getReservationsForGuest = (
  restaurantId: string,
  guestId: string,
): Promise<GuestReservation[]> =>
  api.get(`/restaurants/${restaurantId}/guests/${guestId}/reservations`);

/**
 * Listed prices in integer minor units, before tax, fees and tip. Tables are Peak payments
 * (USD); food and beverage is in the currency of the restaurant's POS.
 */
export interface GuestLifetimeSpend {
  tables: number;
  /** Null without Lightspeed or closed checks: shown as N/A. */
  foodAndBeverage: number | null;
  /** ISO 4217 code of `foodAndBeverage`; null when that is. */
  foodAndBeverageCurrency: string | null;
  /** Null when tables and food and beverage are in different currencies: shown as N/A. */
  total: number | null;
  /** The guest's account creation date. */
  since: string | null;
}

export const getGuestLifetimeSpend = (
  restaurantId: string,
  guestId: string,
): Promise<GuestLifetimeSpend> =>
  api.get(`/restaurants/${restaurantId}/guests/${guestId}/lifetime-spend`);

export const updateGuestNotes = ({
  id,
  restaurantId,
  guestId,
  note,
}: {
  id: string;
  restaurantId: string;
  guestId: string;
  note: string;
}): Promise<void> =>
  api.put(`/restaurants/${restaurantId}/guests/${guestId}/guest-notes/${id}`, {
    note,
  });

export const updateGuestTags = async ({
  restaurantId,
  guestId,
  tags,
}: {
  restaurantId: string;
  guestId: string;
  tags: { id: string | null; title: string }[];
}): Promise<void> =>
  api.put(`/restaurants/${restaurantId}/guests/${guestId}/tags`, {
    tags,
  });

export interface GuestVipTierAssignment {
  vipTier: {
    id: string;
    name: string;
    amountOffTablePercentage: number;
    description: string | null;
  } | null;
  isManuallyApplied: boolean;
}

export const getGuestVipTier = (
  restaurantId: string,
  guestId: string,
): Promise<GuestVipTierAssignment> =>
  api.get(`/restaurants/${restaurantId}/guests/${guestId}/vip-tier`);

export const updateGuestVipTier = async ({
  restaurantId,
  guestId,
  vipTierId,
}: {
  restaurantId: string;
  guestId: string;
  vipTierId: string | null;
}): Promise<void> =>
  api.put(`/restaurants/${restaurantId}/guests/${guestId}/vip-tier`, {
    vipTierId,
  });
